import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import confetti from 'canvas-confetti'

// Components
import { Button } from '@/components/common'
import { UploadZone } from '@/components/upload'
import { ModelSelector } from '@/components/models'
import {
  FENOutput,
  ProgressSteps,
  PerformanceMetrics,
  type Step,
} from '@/components/results'
import { BoardEditor, ChessboardDisplay, ConfidenceHeatmap } from '@/components/board'

// Utils
import { inferFEN } from '@/utils/api'
import { createImagePreview } from '@/utils/helpers'
import type { InferenceResponse } from '@/types/api'

function App() {
  const [darkMode, setDarkMode] = useState(true)

  // Upload state
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  // Model state
  const [selectedModel, setSelectedModel] = useState<string | null>(null)

  // Inference state
  const [isProcessing, setIsProcessing] = useState(false)
  const [results, setResults] = useState<InferenceResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [editedFen, setEditedFen] = useState<string | null>(null)
  const [isEditingBoard, setIsEditingBoard] = useState(false)

  // Progress steps
  const [currentStep, setCurrentStep] = useState<number>(0)

  const steps: Step[] = [
    {
      id: 'upload',
      label: 'Upload',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
      ),
      status: file ? 'completed' : currentStep === 0 ? 'active' : 'pending',
    },
    {
      id: 'detect',
      label: 'Detect',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      ),
      status:
        currentStep >= 2
          ? 'completed'
          : currentStep === 1
            ? 'active'
            : 'pending',
    },
    {
      id: 'process',
      label: 'Process',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
          />
        </svg>
      ),
      status:
        currentStep >= 3
          ? 'completed'
          : currentStep === 2
            ? 'active'
            : 'pending',
    },
    {
      id: 'results',
      label: 'Results',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      status:
        currentStep >= 4
          ? 'completed'
          : currentStep === 3
            ? 'active'
            : 'pending',
    },
  ]

  // Set initial dark mode class
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark')
    } else {
      document.body.classList.remove('dark')
    }
  }, [])

  const toggleTheme = () => {
    setDarkMode(!darkMode)
    document.body.classList.toggle('dark')
  }

  const handleFileSelect = async (selectedFile: File) => {
    try {
      setFile(selectedFile)
      const previewUrl = await createImagePreview(selectedFile)
      setPreview(previewUrl)
      setCurrentStep(1)
      setError(null)
      setResults(null)
      setShowDetails(false)
    } catch (err) {
      console.error('Failed to create preview:', err)
    }
  }

  const handleFileRemove = () => {
    setFile(null)
    setPreview(null)
    setResults(null)
    setError(null)
    setCurrentStep(0)
    setShowDetails(false)
    setEditedFen(null)
    setIsEditingBoard(false)
  }

  const handleInfer = async () => {
    if (!file || !selectedModel) return

    try {
      setIsProcessing(true)
      setError(null)
      setShowDetails(false)
      setCurrentStep(1)

      // Simulate step progression
      setTimeout(() => setCurrentStep(2), 300)

      const response = await inferFEN({ file, model_name: selectedModel })

      setCurrentStep(3)
      setTimeout(() => {
        setResults(response)
        setEditedFen(response.fen_placement)
        setIsEditingBoard(false)
        setCurrentStep(4)

        // Trigger confetti on success
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        })
      }, 300)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inference failed')
      setCurrentStep(0)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-dark-700 bg-white/50 dark:bg-dark-800/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-3xl sm:text-4xl font-bold gradient-text">
                ♟️ chess2fen
              </h1>
              <p className="text-gray-600 dark:text-dark-300 text-sm sm:text-base mt-1">
                Convert chessboard images to FEN notation using deep learning
              </p>
            </motion.div>

            {/* Theme toggle */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              onClick={toggleTheme}
              className="btn-ghost p-3"
              aria-label="Toggle theme"
            >
              {darkMode ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              )}
            </motion.button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        {(file || results) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="mx-auto w-full max-w-4xl">
              <ProgressSteps steps={steps} />
            </div>
          </motion.div>
        )}

        <div className="space-y-10">
          {/* Section 1: Model selection + upload */}
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-50 mb-6">
                Model Selection & Upload
              </h2>

              <div className="space-y-8">
                <div className="max-w-5xl mx-auto">
                  <ModelSelector
                    selectedModel={selectedModel}
                    onModelSelect={setSelectedModel}
                  />
                </div>

                <div className="max-w-3xl mx-auto space-y-6">
                  <UploadZone
                    file={file}
                    preview={preview}
                    onFileSelect={handleFileSelect}
                    onFileRemove={handleFileRemove}
                    disabled={isProcessing}
                  />

                  <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleInfer}
                      disabled={!file || !selectedModel || isProcessing}
                      loading={isProcessing}
                      className="w-full sm:w-auto min-w-[220px]"
                    >
                      {isProcessing ? 'Processing...' : 'Convert to FEN'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={handleFileRemove}
                      disabled={!file && !results && !error}
                      className="w-full sm:w-auto min-w-[180px]"
                    >
                      Reset
                    </Button>
                  </div>

                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
                      >
                        <p className="text-sm text-red-400">{error}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 2: Results */}
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="card p-8 lg:p-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-50 mb-6">
                Results
              </h2>

              <AnimatePresence mode="wait">
                {!results ? (
                  <motion.div
                    key="placeholder"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center py-16 text-center"
                  >
                    <svg
                      className="w-24 h-24 text-gray-300 dark:text-dark-600 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-gray-500 dark:text-dark-400 text-lg">
                      Upload an image to see results
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="results"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-8"
                  >
                    <FENOutput
                      fen={editedFen ?? results.fen_placement}
                      title="Current board FEN"
                    />
                    {editedFen &&
                      editedFen !== results.fen_placement && (
                        <p className="text-xs font-mono text-gray-500 dark:text-dark-400">
                          Predicted: {results.fen_placement}
                        </p>
                      )}

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-600 dark:text-dark-300 uppercase tracking-wider">
                          Image Comparison
                        </h3>
                        <Button
                          variant={isEditingBoard ? 'secondary' : 'primary'}
                          size="sm"
                          onClick={() =>
                            setIsEditingBoard((prev) => !prev)
                          }
                        >
                          {isEditingBoard ? 'Hide edit panel' : 'Edit board'}
                        </Button>
                      </div>

                      <div className="relative">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div className="rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900 p-4">
                            <p className="text-xs font-semibold text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-3">
                              Uploaded Image
                            </p>
                            <div className="aspect-square w-full min-h-[320px] rounded-xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 overflow-hidden">
                              {preview ? (
                                <img
                                  src={preview}
                                  alt="Uploaded preview"
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                  <span className="text-sm text-gray-500 dark:text-dark-400">
                                    No preview available
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900 p-4">
                            <p className="text-xs font-semibold text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-3">
                              Generated Board {editedFen && editedFen !== results.fen_placement && '(Edited)'}
                            </p>
                            <div className="aspect-square w-full min-h-[320px] flex items-center justify-center">
                              <ChessboardDisplay
                                fen={editedFen ?? results.fen_placement}
                                boardWidth={640}
                                showLabel={false}
                              />
                            </div>
                          </div>
                        </div>

                      </div>

                      <AnimatePresence>
                        {isEditingBoard && results && (
                          <motion.div
                            key="board-editor-panel"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                            onClick={() => setIsEditingBoard(false)}
                          >
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="w-full max-w-6xl max-h-[90vh] overflow-y-auto"
                            >
                              <BoardEditor
                                initialFen={results.fen_placement}
                                orientation="white"
                                boardWidth={640}
                                imagePreview={preview}
                                onFenChange={setEditedFen}
                                onClose={() => setIsEditingBoard(false)}
                              />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {results.warnings && results.warnings.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg"
                      >
                        <h4 className="font-semibold text-yellow-400 mb-2">
                          ⚠️ Warnings
                        </h4>
                        <ul className="text-sm text-yellow-300 space-y-1">
                          {results.warnings.map((warning, index) => (
                            <li key={index}>• {warning}</li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.section>
        </div>

        {results && (results.confidence || results.timing_ms) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-10"
          >
            <div className="card">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-50">
                    Detailed Analysis
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-dark-400">
                    Confidence heatmap and performance metrics are hidden by default.
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowDetails((prev) => !prev)}
                >
                  {showDetails ? 'Hide details' : 'Show details'}
                </Button>
              </div>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-6 space-y-6"
                  >
                    {results.confidence && (
                      <ConfidenceHeatmap
                        confidence={results.confidence}
                        compact
                      />
                    )}
                    {results.timing_ms && (
                      <PerformanceMetrics timing={results.timing_ms} />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-dark-700 bg-white/50 dark:bg-dark-800/50 mt-auto">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-600 dark:text-dark-400">
            <span>chess2fen v3.1 · Powered by ONNX Runtime</span>
            <span className="hidden sm:inline">•</span>
            <a
              href="https://github.com/kklike32/chess2fen"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 transition-colors"
            >
              View on GitHub
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App
